import { Select, TextField } from "@nextiva/next-ui";
import cx from "classnames";
import omit from "lodash/omit";
import moment from "moment-timezone";
import React, { Component } from "react";
import DatePicker from "analytics-ui/DatePicker";
import Style from "./DateRow.module.scss";

const DATE_FORMAT = "MM/DD/YYYY";
const MIN_DATE = "10/11/2015";
const MAX_HOURS = 12;
const MAX_MINUTES = 59;

class DateRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.getInputValues(props.date),
      dateError: false,
      hoursError: false,
      minutesError: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { date } = this.props;
    if (!date.isSame(nextProps.date)) {
      this.setState(this.getInputValues(nextProps.date));
    }
  }

  getInputValues = (date) => ({
    hours: date.format("hh"),
    minutes: date.format("mm"),
    meridiem: date.format("a"),
  });

  checkDate = (date) => {
    const { timezone } = this.props;
    if (date === undefined) {
      return false;
    } else if (date === null) {
      return true;
    }
    let selectedDate;
    let error = false;
    const minDate = moment.tz(MIN_DATE, DATE_FORMAT, timezone);

    if (moment.isMoment(date)) {
      selectedDate = date;
    } else {
      selectedDate = moment.tz(date.target.value, DATE_FORMAT, timezone);
    }

    if (selectedDate.isBefore(minDate, "day")) {
      error = true;
    }
    this.setState({ dateError: error });
    return error;
  };

  changeDate = (newDate) => {
    const { date, timezone, onChange, isScheduleMode } = this.props;
    const { meridiem, hoursError, minutesError } = this.state;
    const hasError = this.checkDate(newDate) || hoursError || minutesError;
    if (!hasError) {
      let hours = parseInt(this.state.hours, 10);
      const minutes = parseInt(this.state.minutes, 10);
      if (
        typeof newDate !== "object" ||
        newDate.constructor !== date.constructor
      ) {
        newDate = date;
      }
      if (meridiem === "pm") {
        hours += 12;
      }
      if (hours % 12 === 0) {
        hours -= 12;
      }

      if (isScheduleMode) {
        newDate = moment(newDate).hours(hours).minutes(minutes);
      } else {
        newDate = moment.tz(newDate, timezone).hours(hours).minutes(minutes);
      }
    }
    onChange(newDate, hasError);
  };

  handleChangeHours = (event) => {
    event.preventDefault();
    const hours = parseInt(event.target.value, 10);
    this.setState({
      hours: event.target.value,
      hoursError: isNaN(hours) || hours <= 0 || hours > MAX_HOURS,
    });
  };

  handleBlurHours = () => {
    const { date } = this.props;
    const { hours, hoursError } = this.state;
    if (hoursError || hours === date.format("h")) {
      this.setState({
        hours: date.format("hh"),
        hoursError: false,
      });
    } else {
      this.changeDate();
    }
  };

  handleChangeMinutes = (event) => {
    event.preventDefault();
    const minutes = parseInt(event.target.value, 10);
    this.setState({
      minutes: event.target.value,
      minutesError: isNaN(minutes) || minutes < 0 || minutes > MAX_MINUTES,
    });
  };

  handleBlurMinutes = () => {
    const { date } = this.props;
    const { minutes, minutesError } = this.state;
    if (minutesError || minutes === date.format("m")) {
      this.setState({
        minutes: date.format("mm"),
        minutesError: false,
      });
    } else {
      this.changeDate();
    }
  };

  handleChangeMeridiem = (meridiemObject) => {
    this.setState(
      {
        meridiem: meridiemObject.value,
      },
      this.changeDate,
    );
  };

  render() {
    const { className, label, timezone, datePicker, isScheduleMode, date } =
      this.props;

    const { hours, minutes, meridiem, dateError, hoursError, minutesError } =
      this.state;
    const minDate = moment.tz(MIN_DATE, DATE_FORMAT, timezone);
    const maxDate = moment.tz(timezone);

    const getDatePickerProps = () => {
      return isScheduleMode ? omit(datePicker, ["endDate"]) : datePicker;
    };

    return (
      <div className={cx(className, Style["drp-date-row"])}>
        {dateError && (
          <div className={Style["drp-date-row__error-message"]}>
            Nextiva Analytics only goes back to {MIN_DATE}
          </div>
        )}
        {label && (
          <label className={Style["drp-date-row__label"]}>{label}</label>
        )}
        <div>
          <DatePicker
            className={cx(
              Style["drp-date-row__picker"],
              "drp-date-row__picker",
            )}
            dateFormat={DATE_FORMAT}
            selected={isScheduleMode ? date : date.tz(timezone)}
            minDate={isScheduleMode ? maxDate : minDate}
            maxDate={isScheduleMode ? undefined : maxDate}
            onChange={this.changeDate}
            customInput={<TextField />}
            popperPlacement={isScheduleMode ? "top" : "bottom"}
            {...getDatePickerProps()}
          />
        </div>
        <span className={Style["drp-date-row__at"]}>at</span>
        <TextField
          className={cx(Style["drp-date-row__hours"], {
            "drp-date-row__error": hoursError,
          })}
          min="0"
          max={MAX_HOURS}
          value={hours}
          onChange={this.handleChangeHours}
          onBlur={this.handleBlurHours}
        />
        <span className={Style["drp-date-row__colon"]}>:</span>
        <TextField
          className={cx(Style["drp-date-row__minutes"], {
            "drp-date-row__error": minutesError,
          })}
          min="0"
          max={MAX_MINUTES}
          value={minutes}
          onChange={this.handleChangeMinutes}
          onBlur={this.handleBlurMinutes}
        />
        <Select
          className={cx(
            Style["drp-date-row__meridiem"],
          )}
          portalMenuWrapperClassName="drp-date-row__meridiem--list"
          value={meridiem}
          onChange={this.handleChangeMeridiem}
          options={[
            { value: "am", label: "AM" },
            { value: "pm", label: "PM" },
          ]}
        />
      </div>
    );
  }
}

export default DateRow;
